import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Form, Modal, Select, message, Tag, Row, Col, InputNumber, Card, Statistic, Tabs, DatePicker } from 'antd';
import { PlusOutlined, MinusOutlined, SwapOutlined, EditOutlined, ReloadOutlined, WarningOutlined, ArrowUpOutlined, ArrowDownOutlined, HistoryOutlined } from '@ant-design/icons';
import { inventoryAPI, warehouseAPI, productAPI } from '../../services/api';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const InventoryList = () => {
  const [inventories, setInventories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [transPagination, setTransPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [alertPagination, setAlertPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({ warehouseId: '' });
  const [transFilters, setTransFilters] = useState({ type: '', warehouseId: '' });
  const [activeTab, setActiveTab] = useState('inventory');
  const [stats, setStats] = useState({});

  // Modal状态
  const [stockInModalVisible, setStockInModalVisible] = useState(false);
  const [stockOutModalVisible, setStockOutModalVisible] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [adjustModalVisible, setAdjustModalVisible] = useState(false);

  // 选项数据
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);

  const [stockInForm] = Form.useForm();
  const [stockOutForm] = Form.useForm();
  const [transferForm] = Form.useForm();
  const [adjustForm] = Form.useForm();

  // 类型映射
  const typeMap = {
    'in': { text: '入库', color: 'green' },
    'out': { text: '出库', color: 'red' },
    'transfer': { text: '调拨', color: 'blue' },
    'adjust': { text: '调整', color: 'orange' },
    'check': { text: '盘点', color: 'purple' }
  };

  const alertTypeMap = {
    'low': { text: '库存不足', color: 'red' },
    'high': { text: '库存过高', color: 'orange' }
  };

  const columns = [
    {
      title: '商品编码',
      dataIndex: ['product', 'code'],
      key: 'productCode',
      width: 120
    },
    {
      title: '商品名称',
      dataIndex: ['product', 'name'],
      key: 'productName',
      width: 150
    },
    {
      title: '仓库',
      dataIndex: ['warehouse', 'name'],
      key: 'warehouseName',
      width: 120
    },
    {
      title: '库存数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (text, record) => {
        const unit = record.product?.unit || '';
        return `${parseFloat(text).toLocaleString()} ${unit}`;
      }
    },
    {
      title: '可用数量',
      dataIndex: 'availableQty',
      key: 'availableQty',
      width: 100,
      render: (text, record) => {
        const unit = record.product?.unit || '';
        return <span style={{ color: '#52c41a' }}>{parseFloat(text).toLocaleString()} {unit}</span>;
      }
    },
    {
      title: '锁定数量',
      dataIndex: 'lockedQty',
      key: 'lockedQty',
      width: 100,
      render: (text, record) => {
        const qty = parseFloat(text);
        const unit = record.product?.unit || '';
        return qty > 0 ? <span style={{ color: '#faad14' }}>{qty.toLocaleString()} {unit}</span> : '-';
      }
    },
    {
      title: '平均成本',
      dataIndex: 'avgCost',
      key: 'avgCost',
      width: 100,
      render: (text) => text ? `¥${parseFloat(text).toFixed(2)}` : '-'
    },
    {
      title: '库位',
      dataIndex: 'location',
      key: 'location',
      width: 80,
      render: (text) => text || '-'
    },
    {
      title: '最后入库',
      dataIndex: 'lastInDate',
      key: 'lastInDate',
      width: 100,
      render: (text) => text ? new Date(text).toLocaleDateString() : '-'
    },
    {
      title: '最后出库',
      dataIndex: 'lastOutDate',
      key: 'lastOutDate',
      width: 100,
      render: (text) => text ? new Date(text).toLocaleDateString() : '-'
    }
  ];

  const transactionColumns = [
    {
      title: '流水单号',
      dataIndex: 'transNo',
      key: 'transNo',
      width: 140
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (text) => {
        const type = typeMap[text] || { text, color: 'default' };
        return <Tag color={type.color}>{type.text}</Tag>;
      }
    },
    {
      title: '商品',
      dataIndex: ['product', 'name'],
      key: 'productName',
      width: 150
    },
    {
      title: '仓库',
      dataIndex: ['warehouse', 'name'],
      key: 'warehouseName',
      width: 120
    },
    {
      title: '目标仓库',
      dataIndex: ['targetWarehouse', 'name'],
      key: 'targetWarehouseName',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (text) => {
        const qty = parseFloat(text);
        return (
          <span style={{ color: qty >= 0 ? '#52c41a' : '#ff4d4f' }}>
            {qty >= 0 ? '+' : ''}{qty.toLocaleString()}
          </span>
        );
      }
    },
    {
      title: '变更前',
      dataIndex: 'beforeQty',
      key: 'beforeQty',
      width: 80,
      render: (text) => parseFloat(text).toLocaleString()
    },
    {
      title: '变更后',
      dataIndex: 'afterQty',
      key: 'afterQty',
      width: 80,
      render: (text) => parseFloat(text).toLocaleString()
    },
    {
      title: '操作人',
      dataIndex: ['operator', 'realName'],
      key: 'operatorName',
      width: 100,
      render: (text, record) => text || record.operator?.username || '-'
    },
    {
      title: '操作时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (text) => new Date(text).toLocaleString()
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 150,
      ellipsis: true,
      render: (text) => text || '-'
    }
  ];

  const alertColumns = [
    {
      title: '商品编码',
      dataIndex: 'productCode',
      key: 'productCode',
      width: 120
    },
    {
      title: '商品名称',
      dataIndex: 'productName',
      key: 'productName',
      width: 150
    },
    {
      title: '仓库',
      dataIndex: 'warehouseName',
      key: 'warehouseName',
      width: 120
    },
    {
      title: '当前库存',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (text, record) => `${parseFloat(text).toLocaleString()} ${record.unit || ''}`
    },
    {
      title: '最低库存',
      dataIndex: 'minStock',
      key: 'minStock',
      width: 100,
      render: (text) => text ? parseFloat(text).toLocaleString() : '-'
    },
    {
      title: '最高库存',
      dataIndex: 'maxStock',
      key: 'maxStock',
      width: 100,
      render: (text) => text ? parseFloat(text).toLocaleString() : '-'
    },
    {
      title: '预警类型',
      dataIndex: 'alertType',
      key: 'alertType',
      width: 100,
      render: (text) => {
        const alert = alertTypeMap[text] || { text, color: 'default' };
        return <Tag color={alert.color}>{alert.text}</Tag>;
      }
    }
  ];

  useEffect(() => {
    fetchWarehouses();
    fetchProducts();
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'inventory') {
      fetchInventories();
    } else if (activeTab === 'transactions') {
      fetchTransactions();
    } else if (activeTab === 'alerts') {
      fetchAlerts();
    }
  }, [activeTab, pagination.current, pagination.pageSize, searchText, filters, transPagination.current, transFilters, alertPagination.current]);

  const fetchWarehouses = async () => {
    try {
      const response = await warehouseAPI.getOptions();
      setWarehouses(response || []);
    } catch (error) {
      console.error('获取仓库列表失败', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productAPI.getList({ limit: 1000 });
      setProducts(response.data || []);
    } catch (error) {
      console.error('获取商品列表失败', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await inventoryAPI.getStats();
      setStats(response);
    } catch (error) {
      console.error('获取统计失败', error);
    }
  };

  const fetchInventories = async () => {
    try {
      setLoading(true);
      const response = await inventoryAPI.getList({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        ...filters
      });

      setInventories(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));
    } catch (error) {
      message.error('获取库存列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await inventoryAPI.getTransactions({
        page: transPagination.current,
        limit: transPagination.pageSize,
        ...transFilters
      });

      setTransactions(response.data);
      setTransPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));
    } catch (error) {
      message.error('获取库存流水失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const response = await inventoryAPI.getAlerts({
        page: alertPagination.current,
        limit: alertPagination.pageSize
      });

      setAlerts(response.data);
      setAlertPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));
    } catch (error) {
      message.error('获取库存预警失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    if (activeTab === 'inventory') {
      setPagination(prev => ({
        ...prev,
        current: newPagination.current,
        pageSize: newPagination.pageSize
      }));
    } else if (activeTab === 'transactions') {
      setTransPagination(prev => ({
        ...prev,
        current: newPagination.current,
        pageSize: newPagination.pageSize
      }));
    } else if (activeTab === 'alerts') {
      setAlertPagination(prev => ({
        ...prev,
        current: newPagination.current,
        pageSize: newPagination.pageSize
      }));
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTransFilterChange = (key, value) => {
    setTransFilters(prev => ({ ...prev, [key]: value }));
    setTransPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setSearchText('');
    setFilters({ warehouseId: '' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 入库操作
  const handleStockIn = () => {
    stockInForm.resetFields();
    setStockInModalVisible(true);
  };

  const handleStockInOk = async () => {
    try {
      const values = await stockInForm.validateFields();
      await inventoryAPI.stockIn(values);
      message.success('入库成功');
      setStockInModalVisible(false);
      fetchInventories();
      fetchStats();
      fetchTransactions();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '入库失败');
      }
    }
  };

  // 出库操作
  const handleStockOut = () => {
    stockOutForm.resetFields();
    setStockOutModalVisible(true);
  };

  const handleStockOutOk = async () => {
    try {
      const values = await stockOutForm.validateFields();
      await inventoryAPI.stockOut(values);
      message.success('出库成功');
      setStockOutModalVisible(false);
      fetchInventories();
      fetchStats();
      fetchTransactions();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '出库失败');
      }
    }
  };

  // 调拨操作
  const handleTransfer = () => {
    transferForm.resetFields();
    setTransferModalVisible(true);
  };

  const handleTransferOk = async () => {
    try {
      const values = await transferForm.validateFields();
      await inventoryAPI.transfer(values);
      message.success('调拨成功');
      setTransferModalVisible(false);
      fetchInventories();
      fetchStats();
      fetchTransactions();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '调拨失败');
      }
    }
  };

  // 调整操作
  const handleAdjust = () => {
    adjustForm.resetFields();
    setAdjustModalVisible(true);
  };

  const handleAdjustOk = async () => {
    try {
      const values = await adjustForm.validateFields();
      await inventoryAPI.adjust(values);
      message.success('调整成功');
      setAdjustModalVisible(false);
      fetchInventories();
      fetchStats();
      fetchTransactions();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '调整失败');
      }
    }
  };

  const tabItems = [
    {
      key: 'inventory',
      label: '库存查询',
      children: (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle">
              <Col>
                <Search
                  placeholder="搜索商品编码/名称"
                  onSearch={handleSearch}
                  style={{ width: 200 }}
                  allowClear
                />
              </Col>
              <Col>
                <Select
                  placeholder="选择仓库"
                  style={{ width: 150 }}
                  allowClear
                  value={filters.warehouseId || undefined}
                  onChange={(value) => handleFilterChange('warehouseId', value)}
                >
                  {warehouses.map(wh => (
                    <Option key={wh.id} value={wh.id}>{wh.name}</Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  重置
                </Button>
              </Col>
            </Row>
          </Card>

          <Table
            columns={columns}
            dataSource={inventories}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        </div>
      )
    },
    {
      key: 'transactions',
      label: (
        <span>
          <HistoryOutlined />
          库存流水
        </span>
      ),
      children: (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle">
              <Col>
                <Select
                  placeholder="操作类型"
                  style={{ width: 120 }}
                  allowClear
                  value={transFilters.type || undefined}
                  onChange={(value) => handleTransFilterChange('type', value)}
                >
                  <Option value="in">入库</Option>
                  <Option value="out">出库</Option>
                  <Option value="transfer">调拨</Option>
                  <Option value="adjust">调整</Option>
                </Select>
              </Col>
              <Col>
                <Select
                  placeholder="选择仓库"
                  style={{ width: 150 }}
                  allowClear
                  value={transFilters.warehouseId || undefined}
                  onChange={(value) => handleTransFilterChange('warehouseId', value)}
                >
                  {warehouses.map(wh => (
                    <Option key={wh.id} value={wh.id}>{wh.name}</Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Button icon={<ReloadOutlined />} onClick={() => setTransFilters({ type: '', warehouseId: '' })}>
                  重置
                </Button>
              </Col>
            </Row>
          </Card>

          <Table
            columns={transactionColumns}
            dataSource={transactions}
            rowKey="id"
            loading={loading}
            pagination={{
              ...transPagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`
            }}
            onChange={handleTableChange}
            scroll={{ x: 1400 }}
          />
        </div>
      )
    },
    {
      key: 'alerts',
      label: (
        <span>
          <WarningOutlined />
          库存预警
          {stats.lowStockCount > 0 && (
            <Tag color="red" style={{ marginLeft: 8 }}>{stats.lowStockCount}</Tag>
          )}
        </span>
      ),
      children: (
        <div>
          <Table
            columns={alertColumns}
            dataSource={alerts}
            rowKey="id"
            loading={loading}
            pagination={{
              ...alertPagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`
            }}
            onChange={handleTableChange}
          />
        </div>
      )
    }
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>库存管理</h2>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleStockIn}>
            入库
          </Button>
          <Button icon={<MinusOutlined />} onClick={handleStockOut}>
            出库
          </Button>
          <Button icon={<SwapOutlined />} onClick={handleTransfer}>
            调拨
          </Button>
          <Button icon={<EditOutlined />} onClick={handleAdjust}>
            调整
          </Button>
        </Space>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card>
            <Statistic title="商品种类" value={stats.totalProducts || 0} suffix="种" />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="库存总量" value={stats.totalQuantity || 0} suffix="件" />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="锁定数量"
              value={stats.totalLocked || 0}
              suffix="件"
              valueStyle={{ color: stats.totalLocked > 0 ? '#faad14' : undefined }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="今日入库"
              value={stats.todayIn || 0}
              prefix={<ArrowUpOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="今日出库"
              value={stats.todayOut || 0}
              prefix={<ArrowDownOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="库存预警"
              value={stats.lowStockCount || 0}
              suffix="项"
              valueStyle={{ color: stats.lowStockCount > 0 ? '#ff4d4f' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      {/* 入库弹窗 */}
      <Modal
        title="商品入库"
        open={stockInModalVisible}
        onOk={handleStockInOk}
        onCancel={() => setStockInModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={stockInForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="warehouseId"
                label="入库仓库"
                rules={[{ required: true, message: '请选择仓库' }]}
              >
                <Select placeholder="请选择仓库">
                  {warehouses.map(wh => (
                    <Option key={wh.id} value={wh.id}>{wh.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="productId"
                label="商品"
                rules={[{ required: true, message: '请选择商品' }]}
              >
                <Select
                  placeholder="请选择商品"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {products.map(p => (
                    <Option key={p.id} value={p.id}>{p.code} - {p.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="入库数量"
                rules={[{ required: true, message: '请输入数量' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0.01} precision={2} placeholder="请输入数量" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="unitPrice" label="单价">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} prefix="¥" placeholder="请输入单价" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="subType" label="入库类型">
                <Select placeholder="请选择入库类型">
                  <Option value="purchase">采购入库</Option>
                  <Option value="return">退货入库</Option>
                  <Option value="other">其他入库</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="relatedOrderNo" label="关联单号">
                <Input placeholder="请输入关联单号" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="batchNo" label="批次号">
                <Input placeholder="请输入批次号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="location" label="库位">
                <Input placeholder="请输入库位" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 出库弹窗 */}
      <Modal
        title="商品出库"
        open={stockOutModalVisible}
        onOk={handleStockOutOk}
        onCancel={() => setStockOutModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={stockOutForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="warehouseId"
                label="出库仓库"
                rules={[{ required: true, message: '请选择仓库' }]}
              >
                <Select placeholder="请选择仓库">
                  {warehouses.map(wh => (
                    <Option key={wh.id} value={wh.id}>{wh.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="productId"
                label="商品"
                rules={[{ required: true, message: '请选择商品' }]}
              >
                <Select
                  placeholder="请选择商品"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {products.map(p => (
                    <Option key={p.id} value={p.id}>{p.code} - {p.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="出库数量"
                rules={[{ required: true, message: '请输入数量' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0.01} precision={2} placeholder="请输入数量" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="subType" label="出库类型">
                <Select placeholder="请选择出库类型">
                  <Option value="sales">销售出库</Option>
                  <Option value="return">退货出库</Option>
                  <Option value="scrap">报废出库</Option>
                  <Option value="other">其他出库</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="relatedOrderNo" label="关联单号">
                <Input placeholder="请输入关联单号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="location" label="库位">
                <Input placeholder="请输入库位" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 调拨弹窗 */}
      <Modal
        title="库存调拨"
        open={transferModalVisible}
        onOk={handleTransferOk}
        onCancel={() => setTransferModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={transferForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fromWarehouseId"
                label="源仓库"
                rules={[{ required: true, message: '请选择源仓库' }]}
              >
                <Select placeholder="请选择源仓库">
                  {warehouses.map(wh => (
                    <Option key={wh.id} value={wh.id}>{wh.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="toWarehouseId"
                label="目标仓库"
                rules={[{ required: true, message: '请选择目标仓库' }]}
              >
                <Select placeholder="请选择目标仓库">
                  {warehouses.map(wh => (
                    <Option key={wh.id} value={wh.id}>{wh.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="productId"
                label="商品"
                rules={[{ required: true, message: '请选择商品' }]}
              >
                <Select
                  placeholder="请选择商品"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {products.map(p => (
                    <Option key={p.id} value={p.id}>{p.code} - {p.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="调拨数量"
                rules={[{ required: true, message: '请输入数量' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0.01} precision={2} placeholder="请输入数量" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 调整弹窗 */}
      <Modal
        title="库存调整"
        open={adjustModalVisible}
        onOk={handleAdjustOk}
        onCancel={() => setAdjustModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={adjustForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="warehouseId"
                label="仓库"
                rules={[{ required: true, message: '请选择仓库' }]}
              >
                <Select placeholder="请选择仓库">
                  {warehouses.map(wh => (
                    <Option key={wh.id} value={wh.id}>{wh.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="productId"
                label="商品"
                rules={[{ required: true, message: '请选择商品' }]}
              >
                <Select
                  placeholder="请选择商品"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {products.map(p => (
                    <Option key={p.id} value={p.id}>{p.code} - {p.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="adjustQty"
                label="调整数量"
                rules={[{ required: true, message: '请输入调整数量' }]}
                extra="正数为增加，负数为减少"
              >
                <InputNumber style={{ width: '100%' }} precision={2} placeholder="请输入调整数量" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="reason" label="调整原因">
                <Select placeholder="请选择调整原因">
                  <Option value="check">盘点调整</Option>
                  <Option value="damage">损耗调整</Option>
                  <Option value="error">纠错调整</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InventoryList;
